import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ShuffleIcon from "@mui/icons-material/ShuffleOutlined";
import { Fragment, useState } from "react";
import { useFinance } from "./FinanceContext";
import { clone, metrics, sum } from "./data/model";
import { MONTHS, SEED } from "./data/seedData";
import type { BudgetItem } from "./data/types";
import { AnnualBar } from "./components/AnnualBar";
import { MonthInline } from "./components/MonthPicker";
import { KpiGrid, type KpiItem } from "./components/KpiGrid";
import { StatusBadge, StatusPill } from "./components/StatusChip";
import { toneColor } from "./components/tone";

const BP_PILL: Record<string, string> = {
  Draft: "Draft",
  "Under Review": "Review",
  Approved: "Approved",
  Frozen: "Frozen",
  Published: "Published",
  Rejected: "Rejected",
};

export function BudgetsPage() {
  const {
    model,
    setModel,
    fmt,
    toDisp,
    fromDisp,
    editItem,
    scf,
    selMonth,
    lastChange,
    role,
    openVouchers,
    locks,
    frozenEdits,
    setFrozenEdit,
    has,
    budgetPlan,
    bpSaveDraft,
    bpSubmit,
    bpWithdraw,
    bpDecision,
    bpFreeze,
    bpPublish,
    bpRevision,
    bpDiscard,
    revisionBase,
    setPage,
    scenario,
  } = useFinance();

  const [view, setView] = useState<"annual" | "monthly">("annual");
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [bpCmt, setBpCmt] = useState("");

  const bp = budgetPlan;
  const st = bp.status;

  const query = q.trim().toLowerCase();
  const match = (it: BudgetItem) =>
    !query ||
    it.name.toLowerCase().includes(query) ||
    it.code.toLowerCase().includes(query) ||
    it.led.toLowerCase().includes(query);
  function toggle(l: string) {
    const n = new Set(collapsed);
    if (n.has(l)) n.delete(l);
    else n.add(l);
    setCollapsed(n);
  }

  const cells = lastChange.cells;
  const ck = (id: string, f: string, m: number) => Boolean(cells[id + "|" + f + "|" + m]);
  const canEditDraft = has("budget.edit") && st === "Draft";
  const canEditFrozen = has("budget.editFrozen");
  const feFor = (id: string, f: string, m: number) => frozenEdits[id + "|" + f + "|" + m];

  let tApp = 0;
  let tCmB = 0;
  let tCmA = 0;
  let tYtdB = 0;
  let tYtdA = 0;
  let tRev = 0;
  model.items.forEach((it) => {
    const m = metrics(it, selMonth);
    tApp += it.approved;
    tCmB += m.cmB * scf;
    tCmA += m.cmA;
    tYtdB += m.ytdB * scf;
    tYtdA += m.ytdA;
  });
  model.revenue.forEach((r) => (tRev += r.actual[selMonth] ?? 0));
  const fc = Math.round((tYtdA / (selMonth + 1)) * 12);

  function setTarget(id: string, dispVal: number) {
    setModel((prev) => {
      const n = clone(prev);
      const r = n.items.find((x) => x.id === id);
      if (r) r.annualPlan = fromDisp(dispVal);
      return n;
    });
  }
  function distribute(it: BudgetItem) {
    setModel((prev) => {
      const n = clone(prev);
      const r = n.items.find((x) => x.id === it.id);
      if (!r) return n;
      const lockedSum = r.planned.reduce((s, v, i) => s + (locks[i] ? v : 0), 0);
      const openIdx = r.planned.map((_v, i) => i).filter((i) => !locks[i]);
      const per = openIdx.length
        ? Math.round(((r.annualPlan || 0) - lockedSum) / openIdx.length)
        : 0;
      openIdx.forEach((i) => (r.planned[i] = per < 0 ? 0 : per));
      return n;
    });
  }

  const monthlyKpis: KpiItem[] = [
    { label: "Annual Approved", value: fmt(tApp) },
    { label: `${MONTHS[selMonth]} Budget`, value: fmt(tCmB) },
    { label: `${MONTHS[selMonth]} Actual`, value: fmt(tCmA) },
    { label: `${MONTHS[selMonth]} Revenue`, value: fmt(tRev) },
    {
      label: `${MONTHS[selMonth]} Variance`,
      value: fmt(tCmB - tCmA),
      tone: tCmB - tCmA >= 0 ? "pos" : "neg",
    },
    { label: "YTD Budget", value: fmt(tYtdB) },
    { label: "YTD Actual", value: fmt(tYtdA) },
    { label: "Remaining Budget", value: fmt(tApp - tYtdA) },
    { label: "Forecast", value: fmt(fc) },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Budget Planning{" "}
        {scenario !== "Approved" ? <Chip size="small" label={scenario} sx={{ ml: 1 }} /> : null}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Operational Expenses · {SEED.fiscalYear} ·{" "}
        {role === "Viewer"
          ? "read-only"
          : canEditFrozen
            ? "frozen-period editing enabled (creates versioned revisions)"
            : "frozen periods are locked"}
      </Typography>

      <AnnualBar />

      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        spacing={2}
        sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Financial Year
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{SEED.fiscalYear}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Budget Version
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{bp.version}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Budget Status
          </Typography>
          <Box>
            <StatusPill status={BP_PILL[st] ?? st} />
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {st === "Draft" && has("budget.edit") && (
            <Button size="small" variant="outlined" onClick={bpSaveDraft}>
              Save Draft
            </Button>
          )}
          {st === "Draft" && has("budget.edit") && (
            <Button size="small" variant="contained" onClick={bpSubmit}>
              Submit for Approval
            </Button>
          )}
          {st === "Draft" && revisionBase && has("budget.edit") && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => {
                if (
                  window.confirm(
                    `Discard draft ${bp.version} and revert to ${revisionBase.version} (${revisionBase.status})?`,
                  )
                )
                  bpDiscard();
              }}
            >
              Discard Draft
            </Button>
          )}
          {st === "Draft" && bp.parentVersion && (
            <Button size="small" variant="outlined" onClick={() => setPage("compare")}>
              Compare with {bp.parentVersion}
            </Button>
          )}
          {st === "Under Review" && has("budget.edit") && (
            <Button size="small" variant="outlined" onClick={bpWithdraw}>
              Withdraw Submission
            </Button>
          )}
          {st === "Approved" && has("freeze.month") && (
            <Button size="small" variant="contained" onClick={bpFreeze}>
              Freeze Budget
            </Button>
          )}
          {st === "Frozen" && has("approval.publish") && (
            <Button size="small" variant="contained" onClick={bpPublish}>
              Publish (make Active)
            </Button>
          )}
          {(st === "Frozen" || st === "Published") && has("budget.editFrozen") && (
            <Button size="small" variant="outlined" onClick={bpRevision}>
              Create Revision
            </Button>
          )}
          {(st === "Approved" || st === "Frozen" || st === "Published") && (
            <Button size="small" variant="outlined" onClick={() => setPage("compare")}>
              Compare Versions
            </Button>
          )}
        </Stack>
      </Stack>

      {st === "Draft" ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Draft <strong>{bp.version}</strong>
          {bp.parentVersion ? ` (revision of ${bp.parentVersion})` : ""} is open for editing. Make
          changes, Save Draft, then Submit for Approval — a budget never becomes approved
          automatically.
        </Alert>
      ) : st === "Under Review" ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Budget <strong>{bp.version}</strong> is submitted and awaiting approval — read-only.
        </Alert>
      ) : st === "Approved" ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Budget <strong>{bp.version}</strong> is Approved (workflow complete) — read-only. Freeze
          it to lock the version, then Publish.
        </Alert>
      ) : st === "Frozen" ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Budget <strong>{bp.version}</strong> is Frozen (locked). Publish to make it the active
          version, or Create Revision to open a new editable draft.
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          Budget <strong>{bp.version}</strong> is Published &amp; Active — this is the official
          budget driving the Dashboard, Budget vs Actual and Reports.
        </Alert>
      )}

      {st === "Under Review" ? (
        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Budget Approval — {bp.version}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Lifecycle: Finance Head → Director → CEO (optional). Requires Finance Head + Director
            approval to become Approved.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Approver Level</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(["Finance Head", "Director", "CEO"] as const).map((lvl) => (
                  <TableRow key={lvl}>
                    <TableCell>
                      {lvl}
                      {lvl === "CEO" ? " (optional final)" : ""}
                    </TableCell>
                    <TableCell>
                      <StatusPill status={bp.approvals[lvl] ?? "Pending"} />
                    </TableCell>
                    <TableCell>
                      {role === lvl && !bp.approvals[lvl] && has("approval.act") ? (
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            color="success"
                            variant="contained"
                            onClick={() => bpDecision(lvl, "Approved", bpCmt)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="contained"
                            onClick={() => bpDecision(lvl, "Rejected", bpCmt)}
                          >
                            Reject
                          </Button>
                          {lvl === "Finance Head" && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => bpDecision(lvl, "Changes", bpCmt)}
                            >
                              Request Changes
                            </Button>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {bp.approvals[lvl] ?? (role === lvl ? "—" : `Switch role to "${lvl}"`)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TextField
            sx={{ mt: 1.5 }}
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Comment / reason (attached to reject or request-changes)"
            value={bpCmt}
            onChange={(e) => setBpCmt(e.target.value)}
          />
        </Box>
      ) : null}

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
        Monthly Details · {MONTHS[selMonth]}{" "}
        {locks[selMonth] ? (
          <Chip size="small" sx={{ ml: 1 }} label={`Locked (${locks[selMonth].level})`} />
        ) : null}
      </Typography>
      <KpiGrid items={monthlyKpis} />

      <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search cost head…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button
          size="small"
          variant={view === "annual" ? "contained" : "outlined"}
          onClick={() => setView("annual")}
        >
          Annual Planning
        </Button>
        <Button
          size="small"
          variant={view === "monthly" ? "contained" : "outlined"}
          onClick={() => setView("monthly")}
        >
          Monthly View
        </Button>
        <MonthInline />
        <Button size="small" onClick={() => setCollapsed(new Set())}>
          Expand all
        </Button>
        <Button
          size="small"
          onClick={() => setCollapsed(new Set(SEED.categories.map((c) => c.letter)))}
        >
          Collapse all
        </Button>
      </Stack>

      {view === "monthly" ? (
        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Cost Head</TableCell>
                <TableCell align="right">Approved (RO)</TableCell>
                <TableCell align="right">{MONTHS[selMonth]} Budget</TableCell>
                <TableCell align="right">{MONTHS[selMonth]} Actual</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="right">Forecast</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SEED.categories.map((cat) => {
                const its = model.items.filter((i) => i.catLetter === cat.letter && match(i));
                if (query && !its.length) return null;
                const isCol = collapsed.has(cat.letter) && !query;
                let cApp = 0;
                let cB = 0;
                let cA = 0;
                let cRem = 0;
                let cFc = 0;
                its.forEach((it) => {
                  const m = metrics(it, selMonth);
                  cApp += it.approved;
                  cB += m.cmB * scf;
                  cA += m.cmA;
                  cRem += m.remaining;
                  cFc += m.forecast;
                });
                return (
                  <Fragment key={cat.letter}>
                    <TableRow
                      hover
                      onClick={() => toggle(cat.letter)}
                      sx={{ cursor: "pointer", bgcolor: "action.hover" }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>{cat.code}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {isCol ? "▸" : "▾"} {cat.name}{" "}
                        <Chip size="small" label={cat.type} sx={{ ml: 1 }} />
                      </TableCell>
                      <TableCell align="right">{fmt(cApp)}</TableCell>
                      <TableCell align="right">{fmt(cB)}</TableCell>
                      <TableCell align="right">{fmt(cA)}</TableCell>
                      <TableCell align="right" sx={{ color: toneColor(cRem >= 0 ? "pos" : "neg") }}>
                        {fmt(cRem)}
                      </TableCell>
                      <TableCell align="right">{fmt(cFc)}</TableCell>
                      <TableCell />
                    </TableRow>
                    {!isCol
                      ? its.map((it) => {
                          const m = metrics(it, selMonth);
                          const lk = Boolean(locks[selMonth]);
                          const feP = feFor(it.id, "planned", selMonth);
                          const ckP = ck(it.id, "planned", selMonth);
                          return (
                            <TableRow key={it.id}>
                              <TableCell>{it.code}</TableCell>
                              <TableCell>{it.name}</TableCell>
                              <TableCell align="right">{fmt(it.approved)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{ bgcolor: ckP ? "action.selected" : undefined }}
                              >
                                {!lk && canEditDraft ? (
                                  <TextField
                                    type="number"
                                    size="small"
                                    variant="standard"
                                    value={toDisp(it.planned[selMonth] ?? 0)}
                                    onChange={(e) =>
                                      editItem(it.id, "planned", selMonth, e.target.value)
                                    }
                                    sx={{ width: 110 }}
                                  />
                                ) : lk && canEditFrozen ? (
                                  <Box
                                    onClick={() =>
                                      setFrozenEdit({ it, field: "planned", m: selMonth })
                                    }
                                    sx={{
                                      cursor: "pointer",
                                      color: "warning.main",
                                      textDecoration: "underline dotted",
                                    }}
                                    title={
                                      feP
                                        ? `Modified after freeze · ${feP.ver} · ${feP.reason}`
                                        : "Locked — click to create a revision"
                                    }
                                  >
                                    {fmt((it.planned[selMonth] ?? 0) * scf)}
                                  </Box>
                                ) : (
                                  fmt(m.cmB * scf)
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  cursor: "pointer",
                                  color: "primary.main",
                                  textDecoration: "underline dotted",
                                }}
                                onClick={() => openVouchers(it, "cm")}
                              >
                                {fmt(m.cmA)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: toneColor(m.remaining >= 0 ? "pos" : "neg") }}
                              >
                                {fmt(m.remaining)}
                              </TableCell>
                              <TableCell align="right">{fmt(m.forecast)}</TableCell>
                              <TableCell align="right">
                                <StatusBadge status={m.status} />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      : null}
                  </Fragment>
                );
              })}
              <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
                <TableCell colSpan={2}>GRAND TOTAL</TableCell>
                <TableCell align="right">{fmt(tApp)}</TableCell>
                <TableCell align="right">{fmt(tCmB)}</TableCell>
                <TableCell align="right">{fmt(tCmA)}</TableCell>
                <TableCell
                  align="right"
                  sx={{ color: toneColor(tApp - tYtdA >= 0 ? "pos" : "neg") }}
                >
                  {fmt(tApp - tYtdA)}
                </TableCell>
                <TableCell align="right">{fmt(fc)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: "65vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Cost Head</TableCell>
                  <TableCell align="right">Annual Plan</TableCell>
                  {MONTHS.map((m, i) => (
                    <TableCell key={m} align="right">
                      {locks[i] ? "🔒 " : ""}
                      {m}
                    </TableCell>
                  ))}
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {SEED.categories.map((cat) => {
                  const its = model.items.filter((i) => i.catLetter === cat.letter && match(i));
                  if (!its.length) return null;
                  const isCol = collapsed.has(cat.letter);
                  const cTarget = its.reduce((s, i) => s + (i.annualPlan || 0) * scf, 0);
                  const cAlloc = its.reduce((s, i) => s + sum(i.planned) * scf, 0);
                  return (
                    <Fragment key={cat.letter}>
                      <TableRow
                        hover
                        onClick={() => toggle(cat.letter)}
                        sx={{ cursor: "pointer", bgcolor: "action.hover" }}
                      >
                        <TableCell sx={{ fontWeight: 700 }}>{cat.code}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {isCol ? "▸" : "▾"} {cat.name}
                        </TableCell>
                        <TableCell align="right">{fmt(cTarget)}</TableCell>
                        {MONTHS.map((m, i) => (
                          <TableCell key={m} align="right">
                            {fmt(its.reduce((s, it) => s + (it.planned[i] ?? 0) * scf, 0))}
                          </TableCell>
                        ))}
                        <TableCell align="right">{fmt(cAlloc)}</TableCell>
                        <TableCell />
                      </TableRow>
                      {!isCol
                        ? its.map((it) => {
                            const alloc = sum(it.planned);
                            const target = it.annualPlan || 0;
                            const matched = Math.abs(alloc - target) < 1;
                            return (
                              <TableRow key={it.id}>
                                <TableCell>{it.code}</TableCell>
                                <TableCell>{it.name}</TableCell>
                                <TableCell align="right">
                                  {canEditDraft ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      variant="standard"
                                      value={toDisp(target * scf)}
                                      onChange={(e) =>
                                        setTarget(it.id, Number(e.target.value) / (scf || 1))
                                      }
                                      sx={{ width: 100 }}
                                    />
                                  ) : (
                                    fmt(target * scf)
                                  )}
                                </TableCell>
                                {MONTHS.map((_m, i) => {
                                  const locked = Boolean(locks[i]);
                                  const fe = feFor(it.id, "planned", i);
                                  const changed = ck(it.id, "planned", i);
                                  if (!locked && canEditDraft) {
                                    return (
                                      <TableCell
                                        key={i}
                                        align="right"
                                        sx={{ bgcolor: changed ? "action.selected" : undefined }}
                                      >
                                        <TextField
                                          type="number"
                                          size="small"
                                          variant="standard"
                                          value={toDisp(it.planned[i] ?? 0)}
                                          onChange={(e) =>
                                            editItem(it.id, "planned", i, e.target.value)
                                          }
                                          sx={{ width: 90 }}
                                        />
                                      </TableCell>
                                    );
                                  }
                                  if (locked && canEditFrozen) {
                                    return (
                                      <TableCell
                                        key={i}
                                        align="right"
                                        onClick={() =>
                                          setFrozenEdit({ it, field: "planned", m: i })
                                        }
                                        sx={{
                                          cursor: "pointer",
                                          color: "warning.main",
                                          textDecoration: "underline dotted",
                                        }}
                                        title={
                                          fe
                                            ? `Modified after freeze · ${fe.ver} · ${fe.reason}`
                                            : "Locked — click to create a revision"
                                        }
                                      >
                                        {fmt((it.planned[i] ?? 0) * scf)}
                                      </TableCell>
                                    );
                                  }
                                  return (
                                    <TableCell
                                      key={i}
                                      align="right"
                                      sx={{ color: "text.disabled" }}
                                      title={locked ? `Locked (${locks[i]?.level})` : "read-only"}
                                    >
                                      {fmt((it.planned[i] ?? 0) * scf)}
                                    </TableCell>
                                  );
                                })}
                                <TableCell
                                  align="right"
                                  title={
                                    matched
                                      ? "Reconciles to annual plan"
                                      : `Off by ${fmt((alloc - target) * scf)}`
                                  }
                                >
                                  {fmt(alloc * scf)}
                                  {matched ? " ✓" : " ⚠"}
                                </TableCell>
                                <TableCell align="right">
                                  {canEditDraft ? (
                                    <IconButton
                                      size="small"
                                      title="Distribute annual plan evenly across editable months"
                                      onClick={() => distribute(it)}
                                    >
                                      <ShuffleIcon fontSize="small" />
                                    </IconButton>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Annual Planning — set each cost head&apos;s Annual Plan, then phase it across the 12
            months. Allocated must reconcile to the plan (✓); ⚠ flags a mismatch. Use the shuffle
            icon to auto-distribute the plan evenly across editable months. Frozen months stay
            locked.
          </Alert>
        </>
      )}
    </Box>
  );
}
